import { createSelector } from 'reselect';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
import { AppState, StudioSection } from '../../types/store';
import { SEARCHABLE_SECTIONS } from '../../../constants';

export function getStudioSlice(state: AppState) {
  // we assume that if we are using studio selectors we are in studio and thus there's a state
  return state.studio!;
}

export function getStashSlice(state: AppState) {
  return state.stash!;
}

export const getStudioParams = createSelector(
  [getStudioSlice],
  state => state.params
);

export const getUrlPath = createSelector(
  [getEnv],
  env => new URL(env.requestUrl).pathname
);

export const getIsSubfoldersPage = createSelector(
  [getUrlPath],
  path =>
    String(String(path).split('/').pop() || '').toLowerCase() === 'subfolders'
);

export const getSelectedFolder = createSelector(
  [getStudioSlice],
  state => state.selectedFolder
);

export const getParentFolder = createSelector(
  [getStudioSlice],
  state => state.parentFolder
);

export const getFeaturedFolderId = createSelector(
  [getStudioSlice],
  state => state.featuredFolderId
);

export const getSavedSubmissionsFolderId = createSelector(
  [getStudioSlice],
  state => state.savedSubmissionsFolderId
);

export const getActiveSection = createSelector(
  [getStudioSlice],
  state => state.params.section
);

export const getIsMyTiersPage = createSelector(
  [getActiveSection],
  activeSection => activeSection === StudioSection.PUBLISHED_TIERS
);

export const getIsDraftsSection = createSelector(
  [getActiveSection],
  activeSection =>
    [
      StudioSection.DRAFTS,
      StudioSection.LITERATURE,
      StudioSection.JOURNALS,
      StudioSection.SCHEDULED,
    ].includes(activeSection)
);

export const getIsPublishedSection = createSelector(
  [getActiveSection],
  activeSection =>
    [
      StudioSection.PUBLISHED,
      StudioSection.PUBLISHED_GALLERIES,
      StudioSection.PUBLISHED_TIERS,
    ].includes(activeSection)
);

export const getIsOriginalsSection = createSelector(
  [getActiveSection],
  activeSection =>
    [StudioSection.ORDERS_ORIGINALS, StudioSection.ORDERS_STATUS].includes(
      activeSection
    )
);

export const getPublishedFolderId = createSelector(
  [getActiveSection, getStudioParams],
  (activeSection, params) =>
    activeSection === StudioSection.PUBLISHED && params.subfolderId
      ? parseInt(params.subfolderId)
      : 0
);

export const getPublishedTierId = createSelector(
  [getActiveSection, getStudioParams],
  (activeSection, params) =>
    activeSection === StudioSection.PUBLISHED && params.tierId
      ? parseInt(params.tierId)
      : 0
);

export const getPublishedTiers = createSelector(
  [getStudioSlice],
  state => state.tiers
);

/**
 * Return `true` if the currently active studio section is one that contains unpublished content.
 */
export const getIsUnpublishedDataset = createSelector(
  [getActiveSection],
  activeSection =>
    [
      StudioSection.DRAFTS,
      StudioSection.LITERATURE,
      StudioSection.JOURNALS,
      StudioSection.SCHEDULED,
      StudioSection.PLAYGROUND,
      StudioSection.STASH,
      StudioSection.PRODUCTS_TIER_UNLISTED,
      StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED,
      StudioSection.PRODUCTS_PCP_UNLISTED,
      StudioSection.PRODUCTS_ORIGINAL_UNLISTED,
      StudioSection.APPS_UNLISTED,
    ].includes(activeSection)
);

export const getIsPublishedDataset = createSelector(
  [getActiveSection],
  activeSection =>
    [
      StudioSection.PUBLISHED,
      StudioSection.PUBLISHED_GALLERIES,
      StudioSection.PUBLISHED_TIERS,
      StudioSection.PRODUCTS_TIER,
      StudioSection.PRODUCTS_EXCLUSIVE,
      StudioSection.PRODUCTS_PCP,
      StudioSection.PRODUCTS_ORIGINAL,
      StudioSection.APPS,
    ].includes(activeSection)
);

export const getIsSelectableDataset = createSelector(
  [getActiveSection],
  activeSection =>
    [
      StudioSection.DRAFTS,
      StudioSection.JOURNALS,
      StudioSection.LITERATURE,
      StudioSection.PLAYGROUND,
      StudioSection.PUBLISHED,
      StudioSection.SCHEDULED,
      StudioSection.STASH,
      StudioSection.PRODUCTS_EXCLUSIVE,
      StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED,
      StudioSection.PRODUCTS_PCP,
      StudioSection.PRODUCTS_PCP_UNLISTED,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ].includes(activeSection)
);

export const getTags = createSelector([getStudioSlice], state => state.tags);

export const getTagsForDeviationId = createSelector(
  [getTags, (tags, deviationId: number) => deviationId],
  (tags, deviationId) => tags[deviationId] ?? []
);

export const getNewModalLoading = createSelector(
  [getStudioSlice],
  state => state.newModalLoading ?? false
);

export const getBreadcrumbs = createSelector(
  [getStudioSlice],
  state => state.breadcrumbs
);

export function getIsSearchPage(state: AppState) {
  return (
    SEARCHABLE_SECTIONS.includes(getActiveSection(state)) &&
    !!getStudioParams(state).searchQuery
  );
}

export function getIsTierPage(state: AppState) {
  return !!getStudioParams(state).tierId;
}

export const getIsGridLayoutSupported = createSelector(
  [getActiveSection],
  activeSection =>
    [
      StudioSection.DRAFTS,
      StudioSection.JOURNALS,
      StudioSection.LITERATURE,
      StudioSection.PLAYGROUND,
      StudioSection.PUBLISHED,
      StudioSection.SCHEDULED,
      StudioSection.STASH,
    ].includes(activeSection)
);

export const getIsBoostSupported = createSelector(
  [getActiveSection, getStudioParams],
  (activeSection, params) => {
    if (params.contentType === 'posts') {
      return false;
    }
    return [
      StudioSection.PUBLISHED,
      StudioSection.PRODUCTS_TIER,
      StudioSection.PRODUCTS_PCP,
      StudioSection.PRODUCTS_EXCLUSIVE,
      StudioSection.PRODUCTS_ORIGINAL,
    ].includes(activeSection);
  }
);

export const getIsMergeSupported = createSelector(
  [getActiveSection],
  activeSection =>
    [
      StudioSection.DRAFTS,
      StudioSection.LITERATURE,
      StudioSection.SCHEDULED,
      StudioSection.STASH,
      StudioSection.PLAYGROUND,
    ].includes(activeSection)
);
